import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from 'components/Button/Button';
import Modal, { ModalActions, ModalContent } from 'components/Modal/Modal';
import Form from 'components/form';
import PhoneInput from 'components/form/PhoneInput';
import Select from 'components/form/Select';
import TextField from 'components/form/TextInput';
import COUNTRIES from 'constants/countries';
import useAlert from 'hooks/useAlert';
import React, { useState } from 'react';
import { checkEligibility, requestHost } from 'services/hostNumber.service';
import toast from 'utils/toast';

const { FormItem } = Form;

const RequestForm = ({ form, onSubmit, onCancel, number }) => {
  const { getFieldDecorator, validateFields } = form;

  const handleSubmit = () => {
    return validateFields().then(onSubmit);
  };

  return (
    <>
      <ModalContent>
        <Typography paragraph color="text.secondary">
          Before we can host your number, please check your phone number
          eligibility. This ensures that your number can be successfully hosted
          with us.
        </Typography>
        <Form>
          <Box mb={3}>
            <PhoneInput
              fullWidth
              disabled
              margin="none"
              label="Phone Number"
              value={number}
            />
          </Box>
          <Typography variant="subtitle1" fontWeight={600}>
            Authorization Contact Details
          </Typography>
          <FormItem>
            {getFieldDecorator('contact_title', {
              rules: [{ required: true, message: 'Title is required' }],
            })(<TextField fullWidth label="Title" />)}
          </FormItem>
          <FormItem>
            {getFieldDecorator('email', {
              rules: [{ required: true, message: 'Email is required' }],
            })(<TextField fullWidth label="Email" />)}
          </FormItem>
          <FormItem>
            {getFieldDecorator('contact_phone_number', {
              rules: [{ required: true, message: 'Phone number is required' }],
            })(<PhoneInput fullWidth label="Phone Number" />)}
          </FormItem>
          <FormItem>
            {getFieldDecorator('address_street', {
              rules: [{ required: true, message: 'Address is required' }],
            })(<TextField fullWidth label="Address" />)}
          </FormItem>
          <FormItem>
            {getFieldDecorator('address2')(
              <TextField fullWidth label="Street Address Line 2" />,
            )}
          </FormItem>
          <FormItem>
            {getFieldDecorator('address_city', {
              rules: [{ required: true, message: 'City is required' }],
            })(<TextField fullWidth label="City" />)}
          </FormItem>
          <Grid container spacing={2}>
            <Grid item md={5}>
              <FormItem>
                {getFieldDecorator('address_iso_country', {
                  rules: [{ required: true, message: 'Country is required' }],
                })(
                  <Select
                    fullWidth
                    transformValue
                    menuPosition="fixed"
                    options={COUNTRIES}
                    label="Country"
                  />,
                )}
              </FormItem>
            </Grid>
            <Grid item md={4}>
              <FormItem>
                {getFieldDecorator('address_state', {
                  rules: [{ required: true, message: 'State is required' }],
                })(<TextField fullWidth label="State" />)}
              </FormItem>
            </Grid>
            <Grid item md={3}>
              <FormItem>
                {getFieldDecorator('address_postal_code', {
                  rules: [
                    { required: true, message: 'Postal code is required' },
                  ],
                })(<TextField fullWidth label="Postal Code" />)}
              </FormItem>
            </Grid>
          </Grid>
        </Form>
      </ModalContent>
      <ModalActions>
        <Button variant="text" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={handleSubmit}>Request Authorization</Button>
      </ModalActions>
    </>
  );
};

const WrappedRequestForm = Form.create()(RequestForm);

const HostNumber = ({ onClose }) => {
  const [number, setNumber] = useState('');
  const [error, setError] = useState('');
  const [step, setStep] = useState(1);
  const alert = useAlert();

  const onCheckNumber = async () => {
    try {
      setError();
      const resp = await checkEligibility(number);
      if (resp.eligibility_status === 'ELIGIBLE') {
        setStep(2);
      } else {
        setError(resp.display_message);
      }
    } catch (e) {
      toast.error(e.message);
    }
  };

  const onHost = async (values) => {
    console.log(values);
    try {
      await requestHost({
        ...values,
        phone_number: number,
      });
      alert.confirm({
        severity: 'success',
        title: 'Great',
        description:
          'Email has been sent to authorization contact, once they approve the phone number will be hosted and available for sending text messages. The process could take some time so please be patient.',
        showCancelButton: false,
        confirmationText: 'Got it!',
        onConfirm: () => {
          onClose();
        },
      });
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <Modal
      disableContentWrapper
      maxWidth="sm"
      fullWidth
      title="Host Number"
      onClose={onClose}
    >
      {step === 1 && (
        <>
          <ModalContent>
            <Typography paragraph color="text.secondary">
              Before we can host your number, please check your phone number
              eligibility. This ensures that your number can be successfully
              hosted with us.
            </Typography>
            <PhoneInput
              fullWidth
              label="Phone Number"
              value={number}
              onChange={setNumber}
              helpText={error}
              hasError={!!error}
            />
          </ModalContent>
          <ModalActions>
            <Button variant="text" onClick={onClose}>
              Cancel
            </Button>
            <Button disabled={!number} onClick={onCheckNumber}>
              Check
            </Button>
          </ModalActions>
        </>
      )}
      {step === 2 && (
        <WrappedRequestForm
          onCancel={onClose}
          onSubmit={onHost}
          number={number}
        />
      )}
    </Modal>
  );
};

export default HostNumber;
